import axios, { AxiosInstance, AxiosResponse } from "axios";
import Cookies from "universal-cookie";

export default class BaseService {
    private cookies = new Cookies();
    private instance: AxiosInstance;

    constructor(baseURL: string) {
        this.instance = axios.create({
            baseURL,
        });
    }

    private get accessToken() { return this.cookies.get('access_token') }
    private get refreshToken() { return this.cookies.get('refresh_token') }

    private async getOrRefreshToken(): Promise<any> {
        const instance = axios.create({
            baseURL: process.env.REACT_APP_SERVICE_BASE_PATH_OA_SECURITY,
        });

        const params: Record<string, string> = {
            'grant_type': 'client_credentials'
        }

        const auth = {
            username: process.env.REACT_APP_CLIENT_AUTH_USERNAME!,
            password: process.env.REACT_APP_CLIENT_AUTH_PASSWORD!
        }

        return instance({
            method: auth ? "POST" : "GET",
            url: "/oauth/token",
            params,
            auth
        });
    }

    private getPostConfig(
        url: string,
        method: string,
        data?: any,
        params?: Record<string, string>,
        headers?: Record<string, string>
    ): Record<string, any> {
        return {
            method,
            data,
            headers: {
                Accept: "application/json",
                "Content-type": "application/json",
                "Authorization": `Bearer ${this.accessToken || ''}`,
                ...headers
            },
            url,
            params
        }

    }

    private setCookieAccessAndRefreshToken(result: any) {
        const expires = new Date();
        expires.setTime(expires.getTime() + (result.data.expires_in));

        this.cookies = new Cookies();
        this.cookies.set('access_token', result.data.access_token, {path: '/',  expires});
    }

    async retryAndSetToken(error: any) {
        const originalRequest = error.config;
        const status = error?.response?.status;
        const corsError = status === 0 && error.code === "ERR_NETWORK"; // only dev

        if ((status === 401 && !originalRequest._retry) || (corsError && !originalRequest._retry)) {
            originalRequest._retry = true;
            const result = await this.getOrRefreshToken();
            if (result) {
                this.setCookieAccessAndRefreshToken(result);
                const retryOriginalRequest: any = new Promise((resolve) => {
                    originalRequest.headers.Authorization = `Bearer ${  result.data.access_token}`;
                    return resolve(axios(originalRequest));
                });
                return retryOriginalRequest;
            }
        }
        throw error;
    }

    private addIntercept() {
        this.instance.interceptors
            .response.use(response => response, async (error) => this.retryAndSetToken(error));
    }

    public async checkBearerToken() {
        if ((!this.accessToken && !this.refreshToken) || (!this.accessToken)) {
            const result = await this.getOrRefreshToken();
            if (result) {
                this.setCookieAccessAndRefreshToken(result);
            }
        }
    }

    public async httpPost(url: string, htmlBody?: any, params?: any, headers?: Record<string, string>):
        Promise<AxiosResponse> {
        this.addIntercept();
        return this.instance(this.getPostConfig(url, "POST", htmlBody, params, headers))
    }

    public async httpPatch(url: string, htmlBody?: any, params?: any, headers?: Record<string, string>):
        Promise<AxiosResponse> {
        this.addIntercept();
        return this.instance(this.getPostConfig(url, "PATCH", htmlBody, params, headers))
    }

    public async httpPut(url: string, htmlBody?: any, params?: any, headers?: Record<string, string>):
        Promise<AxiosResponse> {
        this.addIntercept();
        return this.instance(this.getPostConfig(url, "PUT", htmlBody, params, headers))
    }

    public async httpDelete(url: string, htmlBody?: any, params?: any, headers?: Record<string, string>):
        Promise<AxiosResponse> {
        this.addIntercept();
        return this.instance(this.getPostConfig(url, "DELETE", htmlBody, params, headers))
    }

    public async httpGet(url: string, params?: any, headers?: Record<string, string>):
        Promise<AxiosResponse> {
        this.addIntercept();
        return this.instance(this.getPostConfig(url, "GET", {}, params, headers))
    }
}
